import React, { useContext, useEffect, useRef, useState } from 'react';
import { CButton, CCard, CCardBody, CCardHeader, CModal, CModalBody, CModalHeader, CModalTitle } from '@coreui/react';
import { FBToolContext } from 'src/pages/FBTool/index';
import DSelect from 'src/components/Form/DSelect';
import {
    createCanvasPhotos,
    getPageToken,
    getPageVideos,
    postVideo,
    show_permalink_url,
    uploadVideoToPageAPI,
    checkVideoStatus,
} from 'src/pages/FBTool/functions';
import { show_date } from 'src/helpers/date';
import myStorage from 'src/utils/my.storage';
import notify from 'src/utils/notify';
import { delay } from 'src/helpers';
import moment from 'moment';

let interval = null;

function FbToolContent(props) {
    const {
        accessToken,
        pages,
        adAccounts,
        isLoading,
        setIsLoading,
        getAdsTokens,
    } = useContext(FBToolContext);
    const selectPages = Object.keys(pages).map((key) => {
        return { value: key, label: pages[key].name };
    });

    const selectAdAccounts = Object.keys(adAccounts).map((key) => {
        return { value: key, label: adAccounts[key].name + ` - ${key}` };
    });
    const [isPosting, setIsPosting] = useState(false);
    const [isCreatingCanvas, setIsCreatingCanvas] = useState(false);

    const [pageVideos, setPageVideos] = useState({});
    const [videoLogs, setVideoLogs] = useState({});

    const canSelectRows = Object.keys(pageVideos).filter((key) => !pageVideos[key].is_boosts);


    const [isShowModal, setIsShowModal] = useState(false);
    const [isShowCanvasModal, setIsShowCanvasModal] = useState(false);
    const [isShowUploadModal, setIsShowUploadModal] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        myStorage.clear();
        return () => {
            if(interval) clearInterval(interval);
            // Cleanup image URLs to prevent memory leaks
            selectedImages.forEach(url => URL.revokeObjectURL(url));
            // Cleanup video URL
            if(selectedVideo?.url) URL.revokeObjectURL(selectedVideo.url);
        }
    }, []);

    useEffect(() => {
        console.log('PAGE VIDEO CHANGE');

        setSelectedRows(pre => {
            return pre.filter(row => canSelectRows.includes(row));
        });
    }, [pageVideos]);

    const [selectedRows, setSelectedRows] = useState([]);

    const [params, setParams] = useState({
        page_id: null,
        act_id: null,
        limit: 10,
    });
    const [adsToken, setAdsToken] = useState(null);
    const [pageToken, setPageToken] = useState(null);

    const selectedPage = selectPages.find((page) => page.value === params.page_id);
    const selectedAdAccount = selectAdAccounts.find((ad) => ad.value === params.act_id);

    // Auto-set default page and ad account when available
    useEffect(() => {
        if (selectPages.length > 0 && selectAdAccounts.length > 0) {
            const defaultPageId = selectPages[0]?.value;
            const defaultAdAccountId = selectAdAccounts[0]?.value;
            
            // Only set if params are still null (not already set)
            if (!params.page_id && !params.act_id) {
                console.log('Setting default params:', { defaultPageId, defaultAdAccountId });
                setParams(prev => ({
                    ...prev,
                    page_id: defaultPageId,
                    act_id: defaultAdAccountId
                }));
            }
        }
    }, [selectPages, selectAdAccounts, params.page_id, params.act_id]);


    const handleFormSubmit = async (e) => {
        e.preventDefault();
        console.log('Search triggered with params:', params);
        
        // Trigger search directly since params are already up-to-date
        handleParamsChange();
    };
    // Remove auto-trigger on params change since we now trigger manually via Search button
    // useEffect(() => {
    //     if (!params.page_id || !params.act_id) return;
    //     handleParamsChange();
    // }, [params]);

    async function handleParamsChange() {
        if (!params.page_id || !params.act_id) {
            notify.warning('Please select page and ad account');
            return;
        }
        setIsLoading(true);

        await (async () => {
            try {
                const adToken = await getAdsTokens({
                    act_id: params.act_id,
                });

                const pageId = params.page_id;

                const pageToken = await getPageToken({
                    pageId: pageId,
                    token: adToken,
                });
                if (!pageToken) {
                    notify.warning('Page token not found');
                    return;
                }
                const videoResponse = await getPageVideos({
                    token: accessToken,
                    page_id: params.page_id,
                    limit: params.limit,
                    ads_token: adToken,
                    act_id: params.act_id,
                });
                setAdsToken(adToken);
                setPageToken(pageToken);
                console.log(videoResponse?.data);
                setPageVideos(videoResponse?.data ?? {});
            } catch (e) {
                notify.error(e.message);
            }
        })();

        setIsLoading(false);
    }

    async function addLog(video, message, level = 'info') {
        setVideoLogs((pre) => {
            const currentLogs = pre[video.id] ?? [];
            const newLogs = [...currentLogs, {
                time: show_date(new Date(Date.now() + 7 * 60 * 60 * 1000)), // Cộng 7 giờ vào thời gian hiện tại
                level,
                message,
            }];
            return {
                ...pre,
                [video.id]: newLogs,
            };
        });
    }

    /**
     * @typedef {Object} video
     * @property {string} created_time
     * @property {string} description
     * @property {string} id
     * @property {string} permalink_url
     * @property {string} source
     * @property {Object} thumbnails
     * @property {Array} thumbnails.data
     * @property {string} thumbnails.data.uri
     * @param {video} video
     * @param {string} canvas_url
     *
     *
     */
    async function startPostVideo(video, canvas_url) {
        addLog(video, `Start Clone Video`)
        try {
            let imageUrls = video.thumbnails.data.map((thumb) => thumb.uri);
            // get first for testing
            imageUrls = imageUrls.slice(0, 1);


            const data = {
                images: imageUrls,
                ads_token: adsToken,
                acc_id: params.act_id,
                page_id: params.page_id,
                page_token: pageToken,
                open_url_action: canvas_url,
            };

            const { canvasId } = await createCanvasPhotos(data);
            if (!canvasId) {
                notify.error('Create Canvas Photo Failed');
                return;
            }
            addLog(video, `Create Canvas Photo Success: https://fb.com/canvas_doc/${canvasId}`);

            const newCanvasUrl = `https://fb.com/canvas_doc/${canvasId}`;
            const data2 = {
                images: imageUrls,
                ads_token: adsToken,
                acc_id: params.act_id,
                page_id: params.page_id,
                page_token: pageToken,
                open_url_action: newCanvasUrl,
            };

            const { canvasId:canvasId2 } = await createCanvasPhotos(data2);

            addLog(video, `Create Canvas Photo Success: https://fb.com/canvas_doc/${canvasId2}`);

            const image_url = imageUrls[0];
            const message = video.description;


            let detail = {
                acc_id: params.act_id,
                ads_token: adsToken,
                page_id: params.page_id,
                page_token: pageToken,
                canvas_doc_id: canvasId2,
                video_id: video.id,
                image_url,
                message,
                title: message,
                canvas_url,
                page_name: selectedPage.label,
                add_profile_id: selectedAdAccount.value,
            };

            const postVideoResponse = await postVideo(detail);
            if (postVideoResponse.error) {
                addLog(video, `Clone Video Failed: ${postVideoResponse.error.message}`);
            } else {
                addLog(video, `Clone Video Success`);
            }
        } catch (e) {
            addLog(video, `Clone Video Failed: ${e.message}`);
        }
    }


    function handleSelectAll(e) {
        if (e.target.checked) {
            const rows = [];
            for (let key in pageVideos) {
                if (!!pageVideos[key]['is_boosts']) continue;
                rows.push(pageVideos[key].id);
            }
            setSelectedRows(rows);
        } else {
            setSelectedRows([]);
        }
    }

    function handleSelectRow(e, video) {
        if (e.target.checked) {
            setSelectedRows([...selectedRows, video.id]);
        } else {
            setSelectedRows(selectedRows.filter((row) => row !== video.id));
        }
    }

    async function handleSubmit(e) {
        console.log('SUBMIT');
        e.preventDefault();
        const formData = new FormData(e.target);
        const canvas_link = formData.get('canvas_link');
        const delay_minutes = formData.get('delay_minutes');
        const start_after_minutes = formData.get('start_after_minutes');
        if (!canvas_link) {
            notify.warning('Please enter canvas link');
            return;
        }
        if (!delay_minutes || delay_minutes < 0) {
            notify.warning('Please enter delay minutes');
            return;
        }
        setIsShowModal(false);

        const videos = [];
        for (let i = 0; i < selectedRows.length; i++) {
            const video = pageVideos[selectedRows[i]];
            videos.push(video);
            if(start_after_minutes > 0){
                const postTime = new Date(Date.now() + 7 * 60 * 60 * 1000).getTime() +( start_after_minutes * 60 * 1000)
                + (i * delay_minutes * 60 * 1000);
                addLog(video, `Video will be cloned at ${show_date(postTime)}`)
            }

        }
        await delay(start_after_minutes * 60 * 1000)
        handlePostVideos(videos, canvas_link, delay_minutes);

    }

    async function handlePostVideos(video, canvas_link, delay_minutes) {
        setIsPosting(true);
        for (let i = 0; i < video.length; i++) {
            const v = video[i];
            if (i > 0) {
                await delay(delay_minutes * 60 * 1000);
            }
            addLog(v, `Start Clone Video ${i + 1}`)
            await startPostVideo(v, canvas_link);
        }
        setIsPosting(false);
    }

    async function handleAddCanvas(e) {
        e.preventDefault();
        const formData = new FormData(e.target);
        const canvas_link = formData.get('canvas_link');
        const delay_minutes = formData.get('delay_minutes');
        const start_after_minutes = formData.get('start_after_minutes');
        const canvas_images = formData.getAll('canvas_images');
        
        if (!canvas_link) {
            notify.warning('Please enter canvas link');
            return;
        }
        if (!canvas_images || canvas_images.length === 0) {
            notify.warning('Please select images for canvas');
            return;
        }
        if (!delay_minutes || delay_minutes < 0) {
            notify.warning('Please enter delay minutes');
            return;
        }
        
        setIsShowCanvasModal(false);
        setIsCreatingCanvas(true);

        // Convert uploaded files to base64 URLs for Facebook API
        const imageUrls = [];
        for (let file of canvas_images) {
            if (file.size > 0) {
                try {
                    const base64Url = await convertFileToBase64(file);
                    imageUrls.push(base64Url);
                } catch (e) {
                    notify.error(`Failed to process image: ${file.name}`);
                    setIsCreatingCanvas(false);
                    return;
                }
            }
        }

        const videos = [];
        for (let i = 0; i < selectedRows.length; i++) {
            const video = pageVideos[selectedRows[i]];
            videos.push(video);
            if(start_after_minutes > 0){
                const postTime = new Date(Date.now() + 7 * 60 * 60 * 1000).getTime() + (start_after_minutes * 60 * 1000)
                + (i * delay_minutes * 60 * 1000);
                addLog(video, `Canvas will be created at ${show_date(postTime)}`)
            }
        }
        
        await delay(start_after_minutes * 60 * 1000);
        await handleCreateCanvasForVideos(videos, canvas_link, delay_minutes, imageUrls);
    }

    async function handleCreateCanvasForVideos(videos, canvas_link, delay_minutes, imageUrls) {
        for (let i = 0; i < videos.length; i++) {
            const video = videos[i];
            if (i > 0) {
                await delay(delay_minutes * 60 * 1000);
            }
            addLog(video, `Start Creating Canvas ${i + 1}`);
            await createCanvasForVideo(video, canvas_link, imageUrls);
        }
        setIsCreatingCanvas(false);
    }

    async function createCanvasForVideo(video, canvas_url, uploadedImageUrls) {
        addLog(video, `Start Create Canvas with uploaded images`);
        try {
            // Use uploaded images instead of video thumbnails
            const imageUrls = uploadedImageUrls || [];
            
            if (imageUrls.length === 0) {
                addLog(video, 'No images provided for canvas', 'error');
                return;
            }

            addLog(video, `Using ${imageUrls.length} uploaded images for canvas`);

            const data = {
                images: imageUrls,
                ads_token: adsToken,
                acc_id: params.act_id,
                page_id: params.page_id,
                page_token: pageToken,
                open_url_action: canvas_url,
            };

            const { canvasId } = await createCanvasPhotos(data);
            if (!canvasId) {
                addLog(video, 'Create Canvas Failed', 'error');
                return;
            }
            addLog(video, `Create Canvas Success: https://fb.com/canvas_doc/${canvasId}`, 'info');

            // Now attach this canvas to the existing video instead of creating new video
            addLog(video, `Attaching canvas to existing video ${video.id}`);
            
            // Update video state to mark it has canvas attached
            setPageVideos(prev => ({
                ...prev,
                [video.id]: {
                    ...prev[video.id],
                    canvas_id: canvasId,
                    canvas_url: `https://fb.com/canvas_doc/${canvasId}`,
                    is_boosts: true, // Mark as having canvas/boost
                    canvas_link: canvas_url
                }
            }));

            addLog(video, `Canvas attached to video successfully`, 'info');

        } catch (e) {
            addLog(video, `Create Canvas Failed: ${e.message}`, 'error');
        }
    }

    // Utility function to convert file to base64
    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };

    const formRef = useRef();
    const canvasFormRef = useRef();
    const uploadFormRef = useRef();

    useEffect(() => {
        if (!isShowModal) {
            if (formRef.current) formRef.current.reset();
        }
    }, [isShowModal]);

    useEffect(() => {
        if (!isShowCanvasModal) {
            if (canvasFormRef.current) canvasFormRef.current.reset();
            setSelectedImages([]);
        }
    }, [isShowCanvasModal]);

    useEffect(() => {
        if (!isShowUploadModal) {
            setSelectedVideo(null);
        }
    }, [isShowUploadModal]);

    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);
        const imageUrls = files.map(file => URL.createObjectURL(file));
        setSelectedImages(imageUrls);
    };

    const handleVideoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            const videoUrl = URL.createObjectURL(file);
            setSelectedVideo({
                file: file,
                url: videoUrl,
                name: file.name,
                size: file.size
            });
        }
    };

    const handleUploadVideo = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const video_file = formData.get('video_file');
        const video_title = formData.get('video_title');
        const video_description = formData.get('video_description');

        // Validate required fields
        if (!video_file || video_file.size === 0) {
            notify.warning('Please select a video file');
            return;
        }

        if (!video_title) {
            notify.warning('Please enter video title');
            return;
        }

        // Validate video file
        const validVideoTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm'];
        if (!validVideoTypes.includes(video_file.type)) {
            notify.warning(`Unsupported video format: ${video_file.type}. Please use MP4, AVI, MOV, WMV, FLV, or WebM.`);
            return;
        }

        // Check file size (Facebook limit: 4GB)
        const maxSize = 4 * 1024 * 1024 * 1024; // 4GB
        if (video_file.size > maxSize) {
            notify.warning('Video file too large. Maximum size is 4GB.');
            return;
        }

        // Check if page is selected
        console.log('Debug params:', params);
        console.log('Debug page_id:', params.page_id, typeof params.page_id);
        console.log('Debug selectedPage:', selectedPage);
        
        // Get page_id from params or from form if just submitted
        const currentPageId = params.page_id;
        
        if (!currentPageId || currentPageId === 'null' || currentPageId === '') {
            notify.warning('Please select a page first and click Search to initialize');
            return;
        }

        // Check page token - auto initialize if missing
        console.log('Debug pageToken:', pageToken);
        console.log('Debug adsToken:', adsToken);
        console.log('Debug tokens available:', { pageToken, adsToken });
        
        if (!pageToken || !adsToken) {
            console.log('Tokens missing, auto-initializing...');
            notify.info('Initializing tokens for upload...');
            
            try {
                // Auto-initialize tokens like in handleParamsChange
                const adToken = await getAdsTokens({
                    act_id: params.act_id,
                });

                const pageTokenResponse = await getPageToken({
                    pageId: currentPageId,
                    token: adToken,
                });
                
                if (!pageTokenResponse) {
                    notify.error('Failed to get page token for upload');
                    setIsUploading(false);
                    return;
                }
                
                // Set tokens for this upload
                setAdsToken(adToken);
                setPageToken(pageTokenResponse);
                
                console.log('Tokens initialized:', { adToken, pageToken: pageTokenResponse });
                
                // Continue with upload using new tokens
                const uploadData = {
                    video_file: video_file,
                    title: video_title,
                    description: video_description || '',
                    page_id: currentPageId,
                    page_token: pageTokenResponse, // Use new token
                    access_token: accessToken
                };

                const uploadResponse = await uploadVideoToPageAPI(uploadData);
                
                if (uploadResponse.error) {
                    notify.error(`Upload Video Failed: ${uploadResponse.error.message}`);
                } else {
                    notify.success('Video uploaded successfully');
                    // Refresh video list if page is already selected
                    if (params.page_id && params.act_id) {
                        await handleParamsChange();
                    }
                }
                
                setIsUploading(false);
                return;
                
            } catch (e) {
                notify.error(`Failed to initialize tokens: ${e.message}`);
                setIsUploading(false);
                return;
            }
        }

        setIsShowUploadModal(false);
        setIsUploading(true);

        try {
            // Upload using Facebook Video API (chunked upload for large files)
            const uploadData = {
                video_file: video_file, // Send actual file, not base64
                title: video_title,
                description: video_description || '',
                page_id: currentPageId,
                page_token: pageToken,
                access_token: accessToken
            };

            const uploadResponse = await uploadVideoToPageAPI(uploadData);
            
            console.log('Upload response:', uploadResponse);
            
            if (uploadResponse.error) {
                notify.error(`Upload Video Failed: ${uploadResponse.error.message}`);
                console.error('Upload error details:', uploadResponse.error);
            } else {
                notify.success(`Video uploaded successfully! Video ID: ${uploadResponse.video_id}`);
                
                // Check video status and provide proper URLs
                try {
                    const videoStatus = await checkVideoStatus({
                        video_id: uploadResponse.video_id,
                        access_token: accessToken
                    });
                    
                    console.log('Video status check:', videoStatus);
                    
                    if (videoStatus.permalink_url) {
                        notify.info(`Video URL: ${videoStatus.permalink_url}`);
                    } else {
                        // Provide alternative URLs
                        notify.info(`Video accessible at: https://www.facebook.com/${uploadResponse.video_id} or https://www.facebook.com/${currentPageId}/videos/${uploadResponse.video_id}`);
                    }
                    
                    if (videoStatus.status) {
                        console.log('Video processing status:', videoStatus.status);
                        
                        // Check for video errors
                        if (videoStatus.status.video_status === 'error') {
                            notify.error('⚠️ Video upload completed but has processing errors. Video may not be playable.');
                            console.error('Video processing error details:', videoStatus.status);
                        } else if (videoStatus.status.publishing_phase?.status === 'not_started') {
                            notify.warning('📝 Video uploaded but not yet published. May need manual approval.');
                        }
                    }
                    
                } catch (statusError) {
                    console.warn('Could not check video status:', statusError);
                    notify.info(`Video may still be processing. Try: https://www.facebook.com/${currentPageId}/videos`);
                }
                
                // Refresh video list if page is already selected
                if (params.page_id && params.act_id) {
                    await handleParamsChange();
                }
            }
        } catch (e) {
            notify.error(`Upload Video Failed: ${e.message}`);
        }

        setIsUploading(false);
    };

    return (
        <>
            {/* Modal for Clone Video */}
            <CModal visible={isShowModal} backdrop={'static'} size="lg" onClose={() => setIsShowModal(false)}>
                <CModalHeader>
                    <CModalTitle>Clone Videos</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <form onSubmit={handleSubmit} ref={formRef}>
                        <div className="form-group mb-3">
                            <label htmlFor="">Canvas Link</label>
                            <input type="text" className="form-control" name="canvas_link" defaultValue={''} />
                        </div>
                        <div className="form-group mb-3">
                            <label htmlFor="">Clone video after minutes</label>
                            <input type="number" className="form-control" name="start_after_minutes" defaultValue={0} />
                        </div>

                        <div className="form-group mb-3">
                            <label htmlFor="">Delay time (minutes)</label>
                            <input type="number" className="form-control" name="delay_minutes" defaultValue={1} />
                        </div>

                        <div className="text-center">
                            <button className="btn btn-warning px-5" type="submit">
                                Clone Videos
                            </button>
                        </div>
                    </form>
                </CModalBody>
            </CModal>

            {/* Modal for Add Canvas */}
            <CModal visible={isShowCanvasModal} backdrop={'static'} size="lg" onClose={() => setIsShowCanvasModal(false)}>
                <CModalHeader>
                    <CModalTitle>Add Canvas to Videos</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <form onSubmit={handleAddCanvas} ref={canvasFormRef}>
                        <div className="form-group mb-3">
                            <label htmlFor="">Upload Images</label>
                            <input 
                                type="file" 
                                className="form-control" 
                                name="canvas_images" 
                                multiple 
                                accept="image/*"
                                required
                                onChange={handleImageSelect}
                            />
                            <small className="text-muted">Select multiple images for canvas</small>
                            
                            {/* Image Preview */}
                            {selectedImages.length > 0 && (
                                <div className="mt-3">
                                    <label className="form-label">Preview:</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {selectedImages.map((imageUrl, index) => (
                                            <img 
                                                key={index}
                                                src={imageUrl}
                                                alt={`Preview ${index + 1}`}
                                                style={{
                                                    width: '100px',
                                                    height: '100px',
                                                    objectFit: 'cover',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="form-group mb-3">
                            <label htmlFor="">Canvas Link</label>
                            <input type="text" className="form-control" name="canvas_link" defaultValue={''} required />
                            <small className="text-muted">Link to redirect when canvas is clicked</small>
                        </div>
                        
                        <div className="form-group mb-3">
                            <label htmlFor="">Start after minutes</label>
                            <input type="number" className="form-control" name="start_after_minutes" defaultValue={0} />
                        </div>

                        <div className="form-group mb-3">
                            <label htmlFor="">Delay time (minutes)</label>
                            <input type="number" className="form-control" name="delay_minutes" defaultValue={1} />
                        </div>

                        <div className="text-center">
                            <button className="btn btn-primary px-5" type="submit">
                                Add Canvas
                            </button>
                        </div>
                    </form>
                </CModalBody>
            </CModal>

            {/* Modal for Upload Video */}
            <CModal visible={isShowUploadModal} backdrop={'static'} size="lg" onClose={() => setIsShowUploadModal(false)}>
                <CModalHeader>
                    <CModalTitle>Upload Video from Computer</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <form onSubmit={handleUploadVideo} ref={uploadFormRef}>
                        <div className="form-group mb-3">
                            <label htmlFor="">Select Video File</label>
                            <input 
                                type="file" 
                                className="form-control" 
                                name="video_file" 
                                accept="video/*"
                                required
                                onChange={handleVideoSelect}
                            />
                            <small className="text-muted">Select video file from your computer</small>
                            
                            {/* Video Preview */}
                            {selectedVideo && (
                                <div className="mt-3">
                                    <label className="form-label">Preview:</label>
                                    <div className="border p-3 rounded">
                                        <video 
                                            src={selectedVideo.url}
                                            controls
                                            style={{
                                                width: '100%',
                                                maxWidth: '400px',
                                                height: '200px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                        <div className="mt-2">
                                            <small className="text-muted">
                                                File: {selectedVideo.name} ({(selectedVideo.size / 1024 / 1024).toFixed(2)} MB)
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="form-group mb-3">
                            <label htmlFor="">Video Title</label>
                            <input type="text" className="form-control" name="video_title" required />
                        </div>
                        
                        <div className="alert alert-info">
                            <small>
                                <strong>Current Page:</strong> {params.page_id ? (selectedPage?.label || params.page_id) : 'No page selected'}<br/>
                                <strong>Page Token:</strong> {pageToken ? '✓ Available' : '⚠️ Will auto-initialize'}<br/>
                                <strong>Ad Token:</strong> {adsToken ? '✓ Available' : '⚠️ Will auto-initialize'}<br/>
                                <br/>
                                {!pageToken || !adsToken ? (
                                    <div className="text-warning">
                                        <strong>Note:</strong> Tokens will be automatically initialized when you upload.<br/>
                                        You can also click "Search" first to pre-initialize tokens.<br/><br/>
                                    </div>
                                ) : null}
                                <strong>Extension Implementation Required:</strong><br/>
                                This feature uses Facebook Video API:<br/>
                                - Chunked upload for large files (resumable)<br/>
                                - POST /{`{page-id}`}/videos endpoint<br/>
                                - Requires page access token with pages_manage_posts permission<br/>
                                <a href="https://developers.facebook.com/docs/video-api/guides/publishing/" target="_blank">
                                    📖 FB Video API Docs
                                </a>
                            </small>
                        </div>
                        
                        <div className="form-group mb-3">
                            <label htmlFor="">Video Description</label>
                            <textarea 
                                className="form-control" 
                                name="video_description" 
                                rows="4"
                                placeholder="Enter video description (optional)"
                            ></textarea>
                        </div>

                        <div className="text-center">
                            <button className="btn btn-success px-5" type="submit">
                                Upload Video
                            </button>
                        </div>
                    </form>
                </CModalBody>
            </CModal>
            <CCard className="mb-4">
                <CCardHeader>Facebook Tools: <a href="https://drive.google.com/file/d/11HrW1BUkP2l9R0ZwDkCGU1U1bJQ503RK/view?usp=sharing" style={{ marginLeft: '10px', textDecoration: 'none' }}>Download</a> </CCardHeader>
                <CCardBody className="pb-0">
                    <form className="row mb-4" onSubmit={handleFormSubmit}>
                        <div className="col-3">
                            <label htmlFor="">Pages</label>
                            <DSelect 
                                name="page_id" 
                                options={selectPages}
                                value={params.page_id}
                                onChange={(value) => setParams(prev => ({ ...prev, page_id: value }))}
                            />
                        </div>

                        <div className="col-3">
                            <label htmlFor="">Ad Accounts</label>
                            <DSelect 
                                name="act_id" 
                                options={selectAdAccounts}
                                value={params.act_id}
                                onChange={(value) => setParams(prev => ({ ...prev, act_id: value }))}
                            />
                        </div>
                        <div className="col-3">
                            <label htmlFor="">Limit</label>
                            <input 
                                type="number" 
                                name="limit" 
                                className="form-control" 
                                value={params.limit}
                                onChange={(e) => setParams(prev => ({ ...prev, limit: e.target.value }))}
                            />
                        </div>

                        <div className="col-12 mt-2">
                            <CButton type="submit" className="btn-sm" disabled={isLoading}>
                                Search
                            </CButton>
                            <CButton type="button" className="ms-2 btn-sm btn-success"
                                     disabled={isUploading}
                                     onClick={() => setIsShowUploadModal(true)}>
                                Upload Video
                            </CButton>
                            <CButton type="button" className="ms-2 btn-sm btn-primary"
                                     disabled={isLoading || selectedRows.length === 0 || isCreatingCanvas}
                                     onClick={() => setIsShowCanvasModal(true)}>
                                Add Canvas
                            </CButton>
                            <CButton type="button" className="ms-2 btn-sm btn-warning"
                                     disabled={isLoading || selectedRows.length === 0 || isPosting}
                                     onClick={() => setIsShowModal(true)}>
                                Clone Video
                            </CButton>
                        </div>
                    </form>
                </CCardBody>
                <CCardBody className="pt-0">
                    <table className="table table-bordered">
                        <thead>
                        <tr>
                            <th className={'col-form-label-sm'}>
                                <label className={'me-1'}>No.</label>
                                <input className={'form-check-input'} type="checkbox" onChange={handleSelectAll}
                                       checked={selectedRows.length === canSelectRows.length} />
                            </th>
                            <th>Videos</th>
                            <th>Created Time</th>
                            <th>Logs</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Object.keys(pageVideos).map((key, index) => {
                            const video = pageVideos[key];
                            const is_boosts = !!video?.detail?.boosts;
                            return <tr key={video.id}>
                                <td className={'col-form-label-sm'}>
                                    <label className={'me-1'}>{index + 1}. </label>
                                    <input className={'form-check-input col-form-label-sm'} type="checkbox"
                                           disabled={is_boosts}
                                           onChange={(e) => handleSelectRow(e, video)}
                                           checked={selectedRows.includes(video.id)} />
                                </td>
                                <td>
                                    <div className="mb-2">
                                        <a href={show_permalink_url(video.id)}
                                           className={'btn btn-success btn-sm me-2'} target="_blank">Link
                                        </a>
                                        
                                    </div>
                                    <div className="thumbnails">
                                        {(video?.thumbnails?.data ?? []).map((thumb, thumbIndex) => {
                                            // if (thumbIndex > 2) return null;
                                            return (
                                                <img
                                                    style={{
                                                        marginRight: 5,
                                                        objectFit: 'contain',
                                                    }}
                                                    key={thumbIndex}
                                                    src={thumb.uri}
                                                    height={100}
                                                    alt={video.title ?? 'Video'}
                                                    className="thumb"
                                                />
                                            );
                                        })}
                                    </div>
                                    <textarea style={{
                                        marginTop: 10,
                                    }}
                                              className="form-control"
                                              value={video.description ?? ''}
                                              onChange={e => {
                                                  const value = e.target.value;
                                                  setPageVideos(pre => {
                                                      return {
                                                          ...pre,
                                                          [key]: {
                                                              ...pre[key],
                                                              description: value,
                                                          },
                                                      };
                                                  });
                                              }}

                                    />

                                </td>
                                <td>{show_date(new Date(new Date(video.created_time).getTime() + 7 * 60 * 60 * 1000))}
                                    {is_boosts ? <span className="badge bg-warning">Canvas</span> : null}
                                    {video.canvas_id ? (
                                        <div className="mt-1">
                                            <a href={video.canvas_url} target="_blank" className="btn btn-sm btn-info">
                                                View Canvas
                                            </a>
                                            <div className="text-muted small">
                                                Link: {video.canvas_link}
                                            </div>
                                        </div>
                                    ) : null}
                                </td>
                                <td>
                                    {videoLogs[video.id]?.map((log, logIndex) => {
                                        return <div key={logIndex} className={`text-${log.level}`}>
                                            {log.time} - {log.message}
                                        </div>;
                                    })}

                                </td>
                            </tr>;
                        })}
                        </tbody>
                    </table>
                </CCardBody>
            </CCard>
        </>
    );
}

export default FbToolContent;